import { useState } from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';

import Box from '@mui/material/Box';

import PenIcon from '@icons/PenIcon/PenIcon';
import { FormInput } from '@components/FormInput/ui/FormInput';
import { IconButton } from '@components/index';
import CheckIcon from '@icons/CheckIcon/CheckIcon';
import CrossIcon from '@icons/CrossIcon/CrossIcon';
import toast from 'react-hot-toast';
import { Toast } from '@components/Toast/ui/Toast';
import { Version } from '@api/document/model/types';

import { updateDepositionSummary } from '@api/deposition-summary';
import { DocumentType } from '@utils/enums.ts';

import { updateDocumentInstance } from '@api/document-instance';
import { updateUseCase } from '@app/services/api/use-case';
import styles from './EditableTextInput.module.scss';

type UpdateFunction = (id: number | string, data: any) => Promise<any>;

interface RenderEditFieldProps {
  value: string;
  document_type?: string;
  isDocument?: boolean;
  closeEditAction: () => void;
  demandId: Version['document'];
  onSuccess?: () => void;
  size?: 'small' | 'large';
  inputClassName?: string;
  updateFunction?: (
    id: number,
    filename: any,
    callback: (response: any) => void
  ) => Promise<void>;
}

function RenderEditField({
  value,
  closeEditAction,
  demandId,
  onSuccess,
  size,
  inputClassName,
  document_type,
  isDocument,
  updateFunction,
}: RenderEditFieldProps) {
  const { register, getValues } = useForm<{ demandName: string }>({
    defaultValues: {
      demandName: value,
    },
  });

  const updateDocument = async (
    localUpdateFunction: UpdateFunction,
    data: any,
    successMessage: string
  ) => {
    try {
      await localUpdateFunction(demandId, data);

      // fire any user-provided hook
      onSuccess?.();

      // show the toast and close
      setTimeout(() => {
        toast(({ id }) => (
          <Toast
            type="success"
            id={id}
            title="Success update"
            text={successMessage}
          />
        ));
        closeEditAction();
      }, 200);
    } catch (err: any) {
      toast(({ id }) => (
        <Toast
          type="error"
          id={id}
          title="Update failed"
          text={err.message ?? 'Something went wrong'}
        />
      ));
    }
  };

  const onConfirm = async () => {
    if (!getValues('demandName').trim()) {
      toast(({ id }) => (
        <Toast
          type="error"
          id={id}
          title="Error during changing name"
          text="Demand name cannot be empty"
        />
      ));
      return;
    }
    const demandName = getValues('demandName');
    if (isDocument) {
      switch (document_type) {
        case DocumentType.DEPOSITION_SUMMARY:
          await updateDocument(
            updateDepositionSummary,
            { deponent_name: demandName },
            'Deposition summary name has been changed.'
          );
          break;
        case DocumentType.DEMAND_LETTER:
          await updateDocument(
            updateDocumentInstance,
            { filename: demandName },
            'Document name has been changed.'
          );
          break;
        case DocumentType.USE_CASE:
          await updateDocument(
            updateUseCase,
            { client_name: demandName },
            'Document name has been changed.'
          );
          break;
        default:
          console.error('Document type not supported');
          break;
      }
    } else if (updateFunction) {
      updateFunction(demandId, { filename: getValues('demandName') }, (_) => {
        if (onSuccess) {
          onSuccess();
        }
        setTimeout(() => {
          toast(({ id }) => (
            <Toast
              type="success"
              id={id}
              title="Success update"
              text="Document name has been changed."
            />
          ));
          closeEditAction();
        }, 800);
      });
    }
  };

  return (
    <>
      <FormInput {...register('demandName')} className={inputClassName} />
      <IconButton
        variant="active"
        className={[styles.btn, size && styles[`${size}Btn`]].join(' ')}
        onClick={(e) => {
          e.stopPropagation();
          onConfirm();
        }}
      >
        <CheckIcon />
      </IconButton>
      <IconButton
        className={[styles.btn, size && styles[`${size}Btn`]].join(' ')}
        onClick={(e) => {
          e.stopPropagation();
          closeEditAction();
        }}
      >
        <CrossIcon stroke="#98A2B3" />
      </IconButton>
    </>
  );
}

interface EditableTextInputProps {
  value: string;
  wrapperClassName?: string;
  document_type?: string;
  isDocument?: boolean;
  demandId: Version['document'];
  onSuccess?: () => void;
  size?: 'small' | 'large';
  inputClassName?: string;
  editOnHover?: boolean;
  updateFunction?: (
    id: number,
    filename: any,
    callback: (response: any) => void
  ) => Promise<any>;
}

/**
 * @param props - The props for the component.
 * @param value - Text input name.
 * @param document_type - Type of the document.
 * @param wrapperClassName - Custom class name for text input container.
 * @param demandId - Active demand id
 * @param onSuccess - Function which is called after successful demand update
 * @param size - Text input size 'small' | 'large'
 * @description - a component to change demand version
 */
function EditableTextInput({
  wrapperClassName = '',
  document_type,
  value,
  demandId,
  onSuccess,
  size = 'large',
  inputClassName,
  editOnHover,
  isDocument,
  updateFunction,
}: EditableTextInputProps) {
  const [isEditing, setIsEditing] = useState(false);

  const showEditField: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (isEditing) {
      event.stopPropagation();
    }

    if (!editOnHover) {
      setIsEditing(true);
    }
  };

  const showEditFieldPen: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    setIsEditing(true);
  };

  return (
    <Box
      className={[styles.editableTextWrapper, wrapperClassName].join(' ')}
      onClick={showEditField}
    >
      {!isEditing ? (
        <div
          className={classNames(
            styles.editableTextBox,
            editOnHover && styles.showEditOnHover
          )}
        >
          <p>{value}</p>
          <Box onClick={showEditFieldPen}>
            <PenIcon />
          </Box>
        </div>
      ) : (
        <RenderEditField
          demandId={demandId}
          isDocument={isDocument}
          document_type={document_type}
          value={value}
          onSuccess={onSuccess}
          size={size}
          inputClassName={inputClassName}
          closeEditAction={() => {
            setIsEditing(false);
          }}
          updateFunction={updateFunction}
        />
      )}
    </Box>
  );
}

export default EditableTextInput;
